// GENERATED from backend/services/billMemory/core — do not edit; run backend/scripts/sync-bill-memory-core.js
/**
 * Bill Memory — the dictionary translator (source spec, "The starter dictionary").
 *
 * Turns a name into its dictionary key: unit text out first (so "500 gram" is never gram the
 * pulse), then the longest phrase first at word boundaries ("green gram flour" before
 * "gram flour"), then each remaining word to its group key. Words in no group stay as they
 * are. Lessons are never stored under this key, so editing the dictionary can never break a
 * lesson. Pure; copied verbatim to the frontend.
 */
import { makeMatchName, stripItemCode, soundKey } from './matchName';

/** Packaging words that never name an ingredient. "gram", "box", "tin" are NOT here: they can. */
const STANDALONE_UNITS = new Set(['kg', 'kgs', 'gm', 'gms', 'ml', 'ltr', 'ltrs', 'lt', 'pcs', 'pc', 'nos', 'pkt', 'doz',
  'tet', 'tetra', 'pouch', 'packet', 'btl']);

function wordsOf(name) {
  const mn = stripItemCode(makeMatchName(name), name);
  return mn.split(' ').filter((w) => w && !STANDALONE_UNITS.has(w));
}

/**
 * groups: [{ group_key, kind: 'fixed'|'regional', names: [{ text, regional? }] }] — a name marked regional
 * keeps its group but only ever suggests, like a regional group ("gosht" in the mutton group: mutton in
 * many kitchens, any meat in others).
 * optOut: Set of group keys this business switched off.
 * → { phrases, joined, sounds, longest }: joined maps each name with its spaces dropped to its group
 * (null when two groups share it — a joined spelling never chooses between them, Fix 11 part B);
 * sounds maps a name's sound-alike key to its groups (I15, suggestions only).
 */
function buildDictionaryIndex(groups, optOut) {
  const off = optOut instanceof Set ? optOut : new Set(optOut || []);
  const phrases = new Map(); // "w1 w2" → { key, regional }
  const joined = new Map(); // "w1w2" → { key, regional } | null
  const sounds = new Map(); // soundKey → Set<group key>
  let longest = 1;
  for (const g of groups || []) {
    if (!g || off.has(g.group_key)) continue;
    const entry = { key: g.group_key, regional: g.kind === 'regional' };
    for (const n of g.names || []) {
      const text = typeof n === 'string' ? n : n.text;
      const ws = wordsOf(text);
      if (!ws.length) continue;
      const e = n && n.regional === true && !entry.regional ? { key: g.group_key, regional: true } : entry;
      phrases.set(ws.join(' '), e);
      longest = Math.max(longest, ws.length);
      const j = ws.join('');
      const prev = joined.get(j);
      joined.set(j, prev === undefined ? e : prev && prev.key === e.key ? { key: e.key, regional: prev.regional || e.regional } : null);
      const sk = soundKey(text);
      if (sk.length >= 4) {
        if (!sounds.has(sk)) sounds.set(sk, new Set());
        sounds.get(sk).add(g.group_key);
      }
    }
  }
  return { phrases, joined, sounds, longest };
}

function walk(ws, find, maxLen) {
  const out = [];
  const groups = [];
  let regional = false;
  for (let i = 0; i < ws.length;) {
    let hit = null;
    let len = Math.min(maxLen, ws.length - i);
    for (; len >= 1; len--) {
      hit = find(ws.slice(i, i + len));
      if (hit) break;
    }
    if (hit) {
      out.push(...hit.key.split(' '));
      if (!groups.includes(hit.key)) groups.push(hit.key);
      regional = regional || hit.regional;
      i += len;
    } else {
      out.push(ws[i]);
      i += 1;
    }
  }
  return { key: out.join(' '), words: out, groups, regional };
}

/**
 * dictionaryKey("Cashew Whole W240", idx) → { key: "kaju whole w240", words, groups: ["kaju"], regional: false }.
 * With { joined: true } (a bill or typed line, never an item's own key) and only when the normal lookup
 * finds no group: words run together are tried against the groups' joined names ("pesarapappu" →
 * moong dal) — viaJoined then says so.
 */
function dictionaryKey(name, index, { joined = false } = {}) {
  const ws = wordsOf(name);
  if (!index) return { key: ws.join(' '), words: ws, groups: [], regional: false, viaJoined: false };
  const normal = walk(ws, (part) => index.phrases.get(part.join(' ')), index.longest);
  if (!joined || normal.groups.length || !index.joined) return { ...normal, viaJoined: false };
  const viaJoined = walk(ws, (part) => index.joined.get(part.join('')) || null, ws.length);
  return viaJoined.groups.length ? { ...viaJoined, viaJoined: true } : { ...normal, viaJoined: false };
}

export { buildDictionaryIndex, dictionaryKey, STANDALONE_UNITS };
